import React from 'react';
import { Box, Typography, Card, CardMedia, CardContent, Chip, useMediaQuery } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Stagger from './Stagger';
import Reveal from './Reveal';

// Estilos personalizados para las flechas de navegación
const navigationStyles = `
  .swiper-button-next,
  .swiper-button-prev {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    width: 44px !important;
    height: 44px !important;
    margin-top: 0 !important;
    z-index: 50 !important;
    color: #1976d2 !important;
    background: white !important;
    border-radius: 50% !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
    transition: all 0.3s ease !important;
  }
  
  .swiper-button-next:hover,
  .swiper-button-prev:hover {
    background: #f5f5f5 !important;
    transform: translateY(-50%) scale(1.1) !important;
  }
  
  .swiper-button-next {
    right: 8px !important;
  }
  
  .swiper-button-prev {
    left: 8px !important;
  }
  
  .swiper-button-next:after,
  .swiper-button-prev:after {
    font-size: 18px !important;
    font-weight: bold !important;
  }
  
  @media (max-width: 800px) {
    .swiper-button-next,
    .swiper-button-prev {
      display: none !important;
    }
  }
`;

const featured = [
  {
    image: 'https://firebasestorage.googleapis.com/v0/b/landingmunigr.appspot.com/o/Turismo%2FNaturaleza.jpg?alt=media&token=aba4f724-9d14-4b82-9759-6491af427725',
    chip: { label: 'Naturaleza', color: 'success' },
    title: 'Explorá La Granja',
    desc: 'Explorá senderos de trekking, cabalgatas, cicloturismo, espacios culturales donde la comunidad mantiene vivas sus tradiciones.'
  },
  {
    image: 'https://firebasestorage.googleapis.com/v0/b/landingmunigr.appspot.com/o/Turismo%2FGastronom%C3%ADa-destacado.jpeg?alt=media&token=8000eae9-98ff-4a57-b22f-d949abbd0d19',
    chip: { label: 'Gastronomía', color: 'warning' },
    title: 'Sabores Serranos',
    desc: 'La Granja conquista con sus sabores serranos, con platos caseros y el sabor auténtico de las sierras.'
  },
  {
    image: 'https://firebasestorage.googleapis.com/v0/b/landingmunigr.appspot.com/o/Turismo%2FAlojamientos%20habilitados-destacado.jpeg?alt=media&token=b82619c7-e09a-4524-8e1c-65fe915fa139',
    chip: { label: 'Alojamientos habilitados', color: 'primary' },
    title: 'Hospedate en La Granja',
    desc: 'La Granja cuenta con alojamientos habilitados que incluyen cabañas, casas y hoteles, con excelentes servicios para garantizar una estancia segura, cómoda y de calidad.'
  },
  {
    image: 'https://firebasestorage.googleapis.com/v0/b/landingmunigr.appspot.com/o/Turismo%2FEventos-destacado.jpeg?alt=media&token=9616406b-5b20-4b04-987f-7484654594fd',
    chip: { label: 'Eventos', color: 'tertiary' },
    title: 'Eventos en La Granja',
    desc: 'Feria del Libro, 9 de julio, Encuentros de Motos y de Autos, Apertura de temporada, shows en vivo cada fin de semana. Cultura, tradición y diversión para toda la familia.'
  },
  {
    image: 'https://firebasestorage.googleapis.com/v0/b/landingmunigr.appspot.com/o/Turismo%2Fgolf.jpeg?alt=media&token=e24a59b3-9d49-466d-8662-f45038cf563a',
    chip: { label: 'Deportes', color: 'error' },
    title: 'Golf en La Granja',
    desc: 'La Sede Golf Ascochinga es un lugar ideal para los aficionados del golf. Ofrece un campo de golf profesional en un entorno único.'
  },
];

export default function Featured() {
  const isMobile = useMediaQuery('(max-width:800px)');
  const isTablet = useMediaQuery('(max-width:1200px)');

  const cardContent = (item) => (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: 3, 
      maxWidth: 350, 
      margin: '0 auto', 
      height: '420px', // Altura fija para todas las cards
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      transform: 'translate3d(0,0,0)',
      transition: 'transform 220ms ease, box-shadow 220ms ease',
      '&:hover': {
        transform: 'translate3d(0,-6px,0)',
        boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover': { transform: 'none' },
      },
      '&:hover .featured-media': {
        transform: 'scale(1.04)',
      }
    }}>
      <CardMedia
        component="img"
        height="200"
        image={item.image}
        alt={item.title}
        className="featured-media"
        sx={{
          objectFit: 'cover',
          transition: 'transform 600ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          transform: 'scale(1)',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      />
      <CardContent sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: '20px'
      }}>
        <Box>
          <Chip label={item.chip.label} color={item.chip.color} sx={{ mb: 2, fontWeight: 600 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '1.1rem' }}>
            {item.title}
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 4, // Limita a 4 líneas máximo
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.4,
            fontSize: '0.9rem'
          }}
        >
          {item.desc}
        </Typography>
      </CardContent>
    </Card>
  );

  // Configuración del carousel según el tamaño de pantalla
  const getSwiperConfig = () => {
    if (isMobile) {
      return {
        slidesPerView: 1,
        spaceBetween: 16,
        centeredSlides: true
      };
    } else if (isTablet) {
      return {
        slidesPerView: 2,
        spaceBetween: 24,
        centeredSlides: false
      };
    } else {
      return {
        slidesPerView: 3,
        spaceBetween: 32,
        centeredSlides: false
      };
    }
  };

  const swiperConfig = getSwiperConfig();

  return (
    <Box sx={{ py: 8, background: 'transparent' }}>
      <style>{navigationStyles}</style>
      <Box maxWidth={1200} mx="auto" px={2} sx={{ overflow: 'visible' }}>
        <Stagger baseDelay={0} step={110} variants={['slide-right', 'fade-up', 'zoom']} distance={18}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Destacados
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 4, color: 'text.secondary' }}>
            Descubrí rincones llenos de historia, naturaleza y encanto serrano
          </Typography>
          <Box sx={{ position: 'relative', overflow: 'hidden' }}>
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              className="featured-swiper"
              spaceBetween={swiperConfig.spaceBetween}
              slidesPerView={swiperConfig.slidesPerView}
              centeredSlides={swiperConfig.centeredSlides}
              navigation={!isMobile}
              pagination={{ 
                clickable: true,
                dynamicBullets: true
              }}
              autoplay={{ 
                delay: 4000, 
                disableOnInteraction: false,
                pauseOnMouseEnter: true
              }}
              loop={true}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 16,
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 32,
                }
              }}
              style={{ 
                paddingBottom: 40,
                paddingTop: 20
              }}
            >
              {featured.map((item, idx) => (
                <SwiperSlide key={idx}>
                  <Reveal
                    delay={120 + idx * 90}
                    variant={idx % 3 === 0 ? 'slide-left' : idx % 3 === 1 ? 'fade-up' : 'slide-right'}
                    distance={18}
                  >
                    {cardContent(item)}
                  </Reveal>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Stagger>
      </Box>
    </Box>
  );
} 